import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMediaDevices } from "../hooks/useMediaDevices";
import "./DeviceSettingsModal.css";

type TranslateFn = (key: string, fallback: string, params?: Record<string, string>) => string;

interface DeviceSettingsModalProps {
  open: boolean;
  onClose: () => void;
  t: TranslateFn;
  audioInputId: string | null;
  audioOutputId: string | null;
  videoInputId: string | null;
  onSelectAudioInput: (deviceId: string) => void;
  onSelectAudioOutput: (deviceId: string) => void;
  onSelectVideoInput: (deviceId: string) => void;
  /** "Kısayollar" sekmesinin içeriği (2026-10-14) — verilmezse sekme görünmez */
  shortcutsPanel?: ReactNode;
  /** Ses sekmesinin altına eklenecek içerik (SoundPad ses seviyesi vb.) */
  audioExtra?: ReactNode;
  /** [MEMEET-VFX] Video sekmesinin altına eklenecek içerik (arka plan efekti seçici) */
  videoExtra?: ReactNode;
}

type SettingsTab = "audio" | "video" | "shortcuts";

// Kısa iki tonlu "çın" sesini runtime'da WAV data-url olarak üret — asset import
// yok (bundle bloat engellenir), setSinkId ile seçilen hoparlöre yönlendirilebilir.
function generateChimeDataUrl(): string {
  const sampleRate = 44100;
  const duration = 0.45;
  const totalSamples = Math.floor(sampleRate * duration);
  const buffer = new ArrayBuffer(44 + totalSamples * 2);
  const view = new DataView(buffer);

  const writeStr = (offset: number, str: string) => {
    for (let i = 0; i < str.length; i += 1) view.setUint8(offset + i, str.charCodeAt(i));
  };

  writeStr(0, "RIFF");
  view.setUint32(4, 36 + totalSamples * 2, true);
  writeStr(8, "WAVE");
  writeStr(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeStr(36, "data");
  view.setUint32(40, totalSamples * 2, true);

  for (let i = 0; i < totalSamples; i += 1) {
    const time = i / sampleRate;
    const freq = time < 0.22 ? 880 : 1175;
    const localTime = time % 0.22;
    const envelope = Math.sin((Math.PI * localTime) / 0.22);
    const sample = Math.sin(2 * Math.PI * freq * time) * envelope * 0.3;
    const clamped = Math.max(-1, Math.min(1, sample));
    view.setInt16(44 + i * 2, Math.round(clamped * 32767), true);
  }

  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i]);
  return `data:audio/wav;base64,${btoa(binary)}`;
}

function CloseIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function SpeakerIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
      <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
      <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
    </svg>
  );
}

export function DeviceSettingsModal({
  open,
  onClose,
  t,
  audioInputId,
  audioOutputId,
  videoInputId,
  onSelectAudioInput,
  onSelectAudioOutput,
  onSelectVideoInput,
  shortcutsPanel,
  audioExtra,
  videoExtra,
}: DeviceSettingsModalProps) {
  const { audioDevices, videoDevices, outputDevices } = useMediaDevices();
  const [activeTab, setActiveTab] = useState<SettingsTab>("audio");
  const previewVideoRef = useRef<HTMLVideoElement>(null);
  const previewStreamRef = useRef<MediaStream | null>(null);
  const testAudioRef = useRef<HTMLAudioElement>(null);
  const chimeUrl = useMemo(() => generateChimeDataUrl(), []);
  const supportsSinkId = useMemo(
    () => typeof window !== "undefined" && typeof (window.HTMLMediaElement?.prototype as any)?.setSinkId === "function",
    []
  );

  const resolvedAudioInput = audioInputId || audioDevices[0]?.deviceId || "";
  const resolvedAudioOutput = audioOutputId || outputDevices[0]?.deviceId || "";
  const resolvedVideoInput = videoInputId || videoDevices[0]?.deviceId || "";

  // Video sekmesinde seçili kameranın canlı önizlemesi.
  useEffect(() => {
    if (!open || activeTab !== "video") {
      const existing = previewStreamRef.current;
      if (existing) {
        existing.getTracks().forEach((track) => track.stop());
        previewStreamRef.current = null;
      }
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: resolvedVideoInput ? { deviceId: { exact: resolvedVideoInput } } : true,
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        const old = previewStreamRef.current;
        if (old) old.getTracks().forEach((track) => track.stop());
        previewStreamRef.current = stream;
        const el = previewVideoRef.current;
        if (el) {
          el.srcObject = stream;
          el.play().catch(() => undefined);
        }
      } catch {
        // önizleme best-effort — sessiz geç
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, activeTab, resolvedVideoInput]);

  useEffect(() => {
    return () => {
      const existing = previewStreamRef.current;
      if (existing) {
        existing.getTracks().forEach((track) => track.stop());
        previewStreamRef.current = null;
      }
    };
  }, []);

  const handleTestSpeaker = useCallback(async () => {
    const el = testAudioRef.current;
    if (!el) return;
    try {
      const elAny = el as any;
      if (resolvedAudioOutput && typeof elAny.setSinkId === "function") {
        await elAny.setSinkId(resolvedAudioOutput);
      }
    } catch {
      // setSinkId desteklenmiyor / reddedildi — yine de default çıkışta çal
    }
    try {
      el.currentTime = 0;
      await el.play();
    } catch {
      // autoplay reddi — sessiz geç
    }
  }, [resolvedAudioOutput]);

  if (!open) return null;

  return (
    <div
      className="device-settings-backdrop"
      onMouseDown={onClose}
      onContextMenu={(e) => e.preventDefault()}
    >
      <div
        className="device-settings-modal"
        role="dialog"
        aria-modal="true"
        aria-label={t("memeetSettings", "Ayarlar")}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="device-settings-header">
          <h3 className="device-settings-title">{t("memeetSettings", "Ayarlar")}</h3>
          <button
            type="button"
            className="icon-button"
            onClick={onClose}
            title={t("memeetClose", "Kapat")}
            aria-label={t("memeetClose", "Kapat")}
          >
            <CloseIcon />
          </button>
        </div>

        <div className="device-settings-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "audio"}
            className={`device-settings-tab ${activeTab === "audio" ? "active" : ""}`}
            onClick={() => setActiveTab("audio")}
          >
            {t("memeetAudio", "Ses")}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "video"}
            className={`device-settings-tab ${activeTab === "video" ? "active" : ""}`}
            onClick={() => setActiveTab("video")}
          >
            {t("memeetVideo", "Video")}
          </button>
          {shortcutsPanel ? (
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "shortcuts"}
              className={`device-settings-tab ${activeTab === "shortcuts" ? "active" : ""}`}
              onClick={() => setActiveTab("shortcuts")}
            >
              {t("memeetShortcuts", "Kısayollar")}
            </button>
          ) : null}
        </div>

        {activeTab === "shortcuts" && shortcutsPanel ? (
          shortcutsPanel
        ) : activeTab === "audio" ? (
          <div className="device-settings-body">
            <div className="device-settings-row">
              <label className="device-settings-label" htmlFor="device-settings-mic">
                {t("memeetMicrophone", "Mikrofon")}
              </label>
              <select
                id="device-settings-mic"
                value={resolvedAudioInput}
                onChange={(e) => onSelectAudioInput(e.target.value)}
              >
                {audioDevices.length === 0 ? (
                  <option value="">{t("memeetMicrophone", "Mikrofon")}</option>
                ) : (
                  audioDevices.map((device) => (
                    <option key={device.deviceId} value={device.deviceId}>
                      {device.label}
                    </option>
                  ))
                )}
              </select>
            </div>

            <div className="device-settings-row">
              <label className="device-settings-label" htmlFor="device-settings-speaker">
                {t("memeetSpeaker", "Hoparlör")}
              </label>
              <select
                id="device-settings-speaker"
                value={resolvedAudioOutput}
                disabled={!supportsSinkId || outputDevices.length === 0}
                onChange={(e) => onSelectAudioOutput(e.target.value)}
              >
                {outputDevices.length === 0 ? (
                  <option value="">{t("memeetSpeaker", "Hoparlör")}</option>
                ) : (
                  outputDevices.map((device) => (
                    <option key={device.deviceId} value={device.deviceId}>
                      {device.label}
                    </option>
                  ))
                )}
              </select>
              <button
                type="button"
                className="device-settings-test-btn"
                onClick={handleTestSpeaker}
              >
                <SpeakerIcon />
                <span>{t("memeetTestSpeaker", "Test Et")}</span>
              </button>
            </div>
            {audioExtra}
          </div>
        ) : (
          <div className="device-settings-body">
            <div className="device-settings-row">
              <label className="device-settings-label" htmlFor="device-settings-camera">
                {t("memeetCamera", "Kamera")}
              </label>
              <select
                id="device-settings-camera"
                value={resolvedVideoInput}
                onChange={(e) => onSelectVideoInput(e.target.value)}
              >
                {videoDevices.length === 0 ? (
                  <option value="">{t("memeetCamera", "Kamera")}</option>
                ) : (
                  videoDevices.map((device) => (
                    <option key={device.deviceId} value={device.deviceId}>
                      {device.label}
                    </option>
                  ))
                )}
              </select>
            </div>

            <div className="device-settings-preview">
              <video ref={previewVideoRef} autoPlay playsInline muted className="device-settings-preview-video" />
            </div>
            {videoExtra ? <div className="device-settings-row device-settings-vfx">{videoExtra}</div> : null}
          </div>
        )}

        <audio ref={testAudioRef} src={chimeUrl} preload="auto" />
      </div>
    </div>
  );
}
